<script lang="ts">
  import type { ProductDescriptor } from '../lib/products';
  import type { StaleQueuedIntermediate } from '../lib/types';
  import { cachedCatalogue, getInEffectVersion, getQueuedVersions } from '../lib/catalogue-store.svelte';
  import { catalogueState } from '../lib/catalogue-store.svelte';
  import { getPredecessorInEffectAt } from '../lib/manifest-helpers';
  import type { ManifestVersionEntry } from '../lib/types';
  import { projectMarketingBand } from '../lib/surface-projector';
  import { cubeDimensions } from '../lib/cube';
  import { formatLocalDate, formatPercent, formatAmountThousands } from '../lib/formatters';
  import { api } from '../lib/api';
  import { productStaleQueuedCount, productStaleQueuedLabel } from '../lib/queue-staleness-helpers';

  interface Props {
    product: ProductDescriptor;
  }

  const { product }: Props = $props();

  const inEffect = $derived(
    getInEffectVersion(catalogueState.manifest, product.jurisdiction, product.product)
  );
  const queuedVersions = $derived(
    getQueuedVersions(catalogueState.manifest, product.jurisdiction, product.product)
  );
  const catalogue = $derived(cachedCatalogue(product.jurisdiction, product.product));
  const excellentBand = $derived(catalogue ? projectMarketingBand(catalogue, 'excellent') : null);
  const dims = $derived(
    catalogue
      ? cubeDimensions(catalogue.rate_cube, product.pricingModel)
      : null
  );
  const bureauLabel = $derived(catalogue?._dimension_refs?.bureau ?? '(n/a — grade-based)');

  const detailHref = $derived(
    `/catalogue/detail/${product.jurisdiction}/${product.product}`
  );
  // The Queue chip is the natural shortcut to the bucket that lists
  // every queued change across products. Operators reading the chip
  // ("1 change queued") expect to click into that bucket rather than
  // the product Detail editor.
  const approvedQueuedHref = '/changes/approved-queued';

  const queueLabel = $derived.by((): string => {
    if (queuedVersions.length === 0) return 'No changes queued';
    if (queuedVersions.length === 1) return '1 change queued';
    return `${queuedVersions.length} changes queued`;
  });

  // Sort queued entries soonest-first so the next scheduled change is
  // visually the lead item. Approved-but-queued is the only status
  // getQueuedVersions returns; under_review is also included but is a
  // rarer pre-merge state. Capped at three rows so a busy product
  // does not blow out the card height; the trailing "+N more" link is
  // a navigation cue, not a separate page.
  const sortedQueued = $derived(
    [...queuedVersions].sort((a, b) =>
      (a.effective_from.utc < b.effective_from.utc ? -1 : 1)
    )
  );
  const visibleQueued = $derived(sortedQueued.slice(0, 3));
  const queueOverflow = $derived(sortedQueued.length - visibleQueued.length);

  // Each scheduled-changes row navigates to the History tab's
  // comparison view pre-selected with (predecessor ↔ that queued
  // entry) — same deep link the Approved & Queued card's "Compare
  // against current in-effect →" affordance uses (PR #100). The
  // predecessor is the most recent non-dequeued entry whose
  // effective_from precedes the queued entry's, so in the three-
  // queued case (A queued before B, D queued after B) the link
  // resolves to B's predecessor (A or the in-effect), not the
  // current in-effect that already carries D's edits B never saw.
  // Returns null when no predecessor exists at all.
  function compareHref(queued: ManifestVersionEntry): string | null {
    const predecessor = getPredecessorInEffectAt(
      catalogueState.manifest,
      product.jurisdiction,
      product.product,
      queued.effective_from.utc
    );
    if (!predecessor) return null;
    const pair = `${predecessor.git_hash},${queued.git_hash}`;
    return `/history/${product.jurisdiction}/${product.product}?compare=${encodeURIComponent(pair)}`;
  }

  // Stale-queued chip — INTERFACE-SPECIFICATION.md §8.1 surfaced one
  // level up onto the Dashboard tile. For every queued entry on this
  // product the SPA calls /api/manifest-entries/<v>/staleness; an
  // entry whose intermediate list is non-empty is stale (a live edge
  // edit will be silently overwritten when its time-gate fires). The
  // chip aggregates the count and lights up only when at least one
  // queued entry on this product is confirmed stale.
  const staleness = $state<Record<string, StaleQueuedIntermediate[] | undefined>>({});
  const stalenessLoading = $state<Record<string, boolean>>({});

  // Fetch staleness lazily as queued versions appear. Cache-Control:
  // no-store on the proxy worker (PR #123) keeps the response fresh,
  // and the per-cv guard prevents a re-mount loop while the call is
  // in flight. A failed staleness call resolves to [] so the tile
  // does not block the rest of the metrics on a transient proxy
  // error; the operator still sees the queue chip and the scheduled
  // changes rows.
  $effect(() => {
    for (const v of queuedVersions) {
      const cv = v.catalogue_version;
      if (staleness[cv] !== undefined || stalenessLoading[cv]) continue;
      stalenessLoading[cv] = true;
      api.staleQueuedEntry(cv)
        .then((response) => { staleness[cv] = response.intermediate; })
        .catch(() => { staleness[cv] = []; })
        .finally(() => { stalenessLoading[cv] = false; });
    }
  });

  const staleCount = $derived(productStaleQueuedCount(queuedVersions, staleness));
  const staleLabel = $derived(productStaleQueuedLabel(staleCount));
</script>

<article class="product-card">
  <!-- The title is the click target for opening the product Detail
       editor. Each interactive cell (Queue chip, Scheduled changes
       rows) is its own link so nested <a> is avoided and click
       targets stay distinct. The metric blocks themselves stay
       informational; only the Queue chip is clickable inside them. -->
  <header>
    <a class="product-card__title-link" href={detailHref}>
      <div class="product-card__title">
        <span class="product-card__jurisdiction">{product.jurisdiction.toUpperCase()}</span>
        <span class="product-card__label">{product.customerLabel}</span>
      </div>
      <div class="product-card__cube-shape">Cube: {product.cubeShapeLabel}</div>
    </a>
  </header>

  <dl class="product-card__metrics">
    <div>
      <dt>In Effect</dt>
      <dd>{inEffect?.catalogue_version ?? '—'}</dd>
    </div>
    <div>
      <dt>Since</dt>
      <dd>{formatLocalDate(inEffect?.effective_from.local)}</dd>
    </div>
    <div class="product-card__metric-with-link">
      <dt>Queue</dt>
      <dd>
        <a class="product-card__queue-chip" href={approvedQueuedHref}>
          <span class="status-indicator" class:has-queue={queuedVersions.length > 0}>
            {queuedVersions.length > 0 ? '●' : '○'}
          </span>
          {queueLabel}
        </a>
        {#if staleCount > 0}
          <a
            class="product-card__stale-chip"
            href={approvedQueuedHref}
            title="One or more queued changes will silently overwrite a live edit when they activate. Click to review."
          >
            <span aria-hidden="true">⚠</span>
            {staleLabel}
          </a>
        {/if}
      </dd>
    </div>
  </dl>

  <dl class="product-card__metrics product-card__metrics--hero">
    <div>
      <dt>Excellent (min)</dt>
      <dd class="num">{formatPercent(excellentBand?.min_rate)}</dd>
    </div>
    <div>
      <dt>Loan range</dt>
      <dd class="num">
        {#if catalogue}
          ${formatAmountThousands(catalogue.loan_amount_range.min)} – ${formatAmountThousands(catalogue.loan_amount_range.max)}
        {:else}
          —
        {/if}
      </dd>
    </div>
    <div>
      <dt>Cube</dt>
      <dd>
        {#if dims}
          {#if dims.bands !== undefined}
            {dims.bands} bands × {dims.housings} hsg × {dims.channels} chan
          {:else}
            {dims.grades} grade-subgrade × {dims.channels} chan
          {/if}
        {:else}
          —
        {/if}
      </dd>
    </div>
    <div>
      <dt>Bureau</dt>
      <dd class="muted">{bureauLabel}</dd>
    </div>
  </dl>

  <p class="product-card__open-detail">
    <a href={detailHref}>Open Catalogue → Detail →</a>
  </p>

  {#if visibleQueued.length > 0}
    <!-- Scheduled changes — surfaces queued entries on the dashboard
         so a reviewer scanning products at a glance sees what is
         coming up rather than having to drill into Changes → Approved
         & Queued. Soonest first, capped at three rows; each row is
         its own link to the History compare view (current in-effect
         ↔ that queued entry). The trailing "+N more" cue points to
         the full Approved & Queued list. -->
    <section class="product-card__queue" aria-label="Scheduled changes">
      <h4 class="product-card__queue-heading">Scheduled changes</h4>
      <ul class="product-card__queue-list">
        {#each visibleQueued as queued (queued.catalogue_version)}
          {@const href = compareHref(queued)}
          <li>
            {#if href}
              <a class="product-card__queue-row" {href}>
                <span class="product-card__queue-version"><code>{queued.catalogue_version}</code></span>
                {#if queued.value_type}
                  <span class="muted">· {queued.value_type}</span>
                {/if}
                <span class="muted">· {formatLocalDate(queued.effective_from.local)}</span>
                {#if queued.approved_by}
                  <span class="muted">· {queued.approved_by}</span>
                {/if}
              </a>
            {:else}
              <span class="product-card__queue-row product-card__queue-row--inert">
                <span class="product-card__queue-version"><code>{queued.catalogue_version}</code></span>
                {#if queued.value_type}
                  <span class="muted">· {queued.value_type}</span>
                {/if}
                <span class="muted">· {formatLocalDate(queued.effective_from.local)}</span>
                {#if queued.approved_by}
                  <span class="muted">· {queued.approved_by}</span>
                {/if}
              </span>
            {/if}
          </li>
        {/each}
      </ul>
      {#if queueOverflow > 0}
        <a class="product-card__queue-more" href={approvedQueuedHref}>
          +{queueOverflow} more · open Changes → Approved & Queued
        </a>
      {/if}
    </section>
  {/if}
</article>

<style>
  .product-card {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    background: var(--surface-card);
    border: 1px solid var(--surface-line-soft);
    border-radius: var(--radius-md);
    padding: var(--space-5);
    color: var(--surface-fg);
    transition: border-color 120ms ease;
  }
  .product-card:hover { border-color: var(--surface-line); }
  /* Title click → Detail. The title gets its own <a> rather than
     wrapping the whole card so the Queue chip and the Scheduled
     changes rows can be their own click targets without nested
     <a> elements (invalid HTML). A small "Open Catalogue → Detail
     →" affordance at the bottom of the card preserves the
     full-card-click expectation for operators who hit the wrong
     row. */
  .product-card__title-link {
    text-decoration: none;
    color: inherit;
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
  .product-card__title-link:hover .product-card__label { text-decoration: underline; }
  .product-card header { display: flex; flex-direction: column; gap: var(--space-1); }
  .product-card__open-detail {
    margin: 0;
    font-size: var(--type-sm);
    text-align: right;
  }
  .product-card__open-detail a {
    color: var(--accent-info);
    text-decoration: none;
  }
  .product-card__open-detail a:hover { text-decoration: underline; }
  .product-card__title {
    display: flex;
    align-items: baseline;
    gap: var(--space-2);
  }
  .product-card__jurisdiction {
    font-family: var(--font-mono);
    font-size: var(--type-sm);
    color: var(--surface-muted);
    letter-spacing: 0.05em;
  }
  .product-card__label {
    font-size: var(--type-md);
    font-weight: 600;
  }
  .product-card__cube-shape {
    font-size: var(--type-sm);
    color: var(--surface-muted);
    font-family: var(--font-mono);
  }
  .product-card__metrics {
    margin: 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--space-3);
  }
  .product-card__metrics--hero {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    border-top: 1px solid var(--surface-line-soft);
    padding-top: var(--space-3);
  }
  .product-card__metrics > div {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    min-width: 0;
  }
  .product-card__metrics dt {
    font-size: var(--type-sm);
    color: var(--surface-muted);
  }
  .product-card__metrics dd {
    margin: 0;
    font-size: var(--type-base);
  }
  .product-card__metrics .num {
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
  }
  .status-indicator {
    color: var(--surface-faint);
    margin-right: var(--space-1);
  }
  .status-indicator.has-queue { color: var(--accent-info); }

  /* Queue chip: nested link inside the metrics dl that opens Changes
     → Approved & Queued. The text matches the rest of the dd cell so
     the chip reads as continuous prose rather than a button-style
     affordance; the underline-on-hover signals it is clickable. */
  .product-card__queue-chip {
    text-decoration: none;
    color: inherit;
    display: inline-flex;
    align-items: baseline;
  }
  .product-card__queue-chip:hover { text-decoration: underline; }
  .product-card__metric-with-link dd { font-size: var(--type-base); }

  /* Stale-queued chip — sits inline with the queue chip, amber pill
     so it reads as a warning at a glance without shouting. Matches
     the staleness-banner colour family on ApprovedQueuedList so the
     two surfaces feel like one signal. Click target is identical to
     the queue chip (Approved & Queued) because the per-card banner
     in that view is the right place to investigate; the chip exists
     to draw the operator there, not to be a separate destination. */
  .product-card__stale-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-left: var(--space-2);
    padding: 2px 8px;
    border-radius: 999px;
    background: #FFF4E0;
    border: 1px solid #F4A100;
    color: #7A4F00;
    font-size: var(--type-xs);
    font-weight: 600;
    text-decoration: none;
    line-height: 1.4;
  }
  .product-card__stale-chip:hover { background: #FFE7B5; }

  /* Scheduled changes panel — sits under the hero metrics with a
     subtle separator above it. Rendered only when at least one
     queued entry exists so a clean product does not show an empty
     section. */
  .product-card__queue {
    border-top: 1px solid var(--surface-line-soft);
    padding-top: var(--space-3);
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
  .product-card__queue-heading {
    margin: 0 0 var(--space-1) 0;
    font-size: var(--type-sm);
    font-weight: 600;
    color: var(--surface-muted);
    letter-spacing: 0.01em;
  }
  .product-card__queue-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
  .product-card__queue-list li {
    font-size: var(--type-sm);
  }
  /* Each row is a link to the History compare view (current in-effect
     ↔ that queued entry). Rendered as a plain colour with hover
     underline so the row reads like the rest of the metadata rather
     than shouting like a primary button. */
  .product-card__queue-row {
    display: inline;
    text-decoration: none;
    color: inherit;
  }
  .product-card__queue-row:hover { text-decoration: underline; }
  .product-card__queue-row--inert { cursor: default; }
  .product-card__queue-version code {
    font-family: var(--font-mono);
  }
  .product-card__queue-more {
    display: inline-block;
    margin: var(--space-1) 0 0 0;
    font-size: var(--type-xs);
    font-style: italic;
    color: var(--surface-muted);
    text-decoration: none;
  }
  .product-card__queue-more:hover { text-decoration: underline; }

  /* Below 480 px the three- and four-column metric grids crowd the
   * numeric values. Reflow to two columns so each metric keeps a
   * readable column-width; the cube-shape and hero rows both fit
   * cleanly. The card itself stays full-width inside the Dashboard
   * grid (which collapses to 1fr below 768 px). */
  @media (max-width: 480px) {
    .product-card__metrics,
    .product-card__metrics--hero {
      grid-template-columns: 1fr 1fr;
    }
  }
</style>
